import { JALALI_MONTH_NAMES, MAX_JALALI_YEAR, MIN_JALALI_YEAR, PERSIAN_WEEKDAY_SHORT, TEHRAN_TIME_ZONE, addJalaliDays, addJalaliMonths, formatJalali, fromJalali, gregorianToJalali, jalaliDaysBetween, jalaliMonthLength, toEnglishDigits, toJalali, } from '@bookora/shared';
import dayjs from 'dayjs';
/**
 * A Jalali calendar for AntD's DatePicker family.
 *
 * AntD renders whatever calendar its `GenerateConfig` describes: `getYear`,
 * `getMonth` and `getDate` are asked for the *displayed* numbers, and the
 * setters are handed the numbers the user clicked. Return Jalali from the
 * getters and interpret Jalali in the setters and the whole picker — panel
 * headers, month grid, keyboard navigation, range highlighting — becomes
 * Jalali, while the value flowing in and out stays an ordinary instant.
 *
 * ── THE ARITHMETIC IS NOT dayjs's ───────────────────────────────────────────
 *
 * Every calendar operation delegates to `@bookora/shared`'s Jalali kernel — the
 * same code the backend's reporting periods and the reminder scheduler use.
 * "One month after ۳۱ تیر" has to mean the same thing in the picker as it does
 * in a reminder rule, and the only way to guarantee that is to have one
 * implementation. dayjs is used solely as the value container.
 *
 * ── TIME ZONE: TEHRAN, NOT THE LAPTOP'S ─────────────────────────────────────
 *
 * Every getter and setter below — the day a cell stands for, which cell is
 * «today», the hour a time column shows — resolves in Asia/Tehran, whatever
 * zone the browser is in.
 *
 * It used to be the browser's zone, on the argument that a cell must agree
 * with the `hour()` dayjs itself reports. That kept the picker consistent with
 * dayjs and wrong about the clinic: on a laptop set to Dubai (the walkthrough
 * machine, and plenty of Iranian office PCs since DST was abolished) the
 * «today» cell and the day a click produced were Dubai's, and a package sold
 * on the morning of ۴ مهر reached the server as ۳ مهر. Every stored date in
 * this product is a Tehran day, `<JalaliDate>` displays in Tehran, and the
 * API reads a date-only string as a Tehran day — so the picker does too.
 *
 * The one consequence for callers: dayjs's OWN day and wall-clock methods
 * (`startOf('day')`, `format('YYYY-MM-DD')`, `.hour()`, `isAfter(x, 'day')`)
 * still answer in the browser's zone. Convert a picked value with
 * `toApiDate()` / `startOfTehranDay()` and compare with
 * `isBeforeTehranToday()` and friends from `./tehran-day.ts`; never with
 * dayjs's unit arithmetic. On a Tehran machine the two agree, which is exactly
 * why the difference went unnoticed.
 */
const TZ = TEHRAN_TIME_ZONE;
function parts(value) {
    const j = toJalali(value.toDate(), TZ);
    return {
        jy: j.jy,
        jm: j.jm,
        jd: j.jd,
        hour: j.hour,
        minute: j.minute,
        second: j.second,
        millisecond: value.millisecond(),
    };
}
function clampYear(jy) {
    return Math.min(MAX_JALALI_YEAR, Math.max(MIN_JALALI_YEAR, jy));
}
/**
 * The window a Jalali year in *this product* can fall in — patient birth dates
 * at one end, subscription end dates at the other. Used only to tell a Jalali
 * string apart from a Gregorian one; the pickers themselves accept the kernel's
 * full range.
 */
const PLAUSIBLE_JALALI_MIN = 1200;
const PLAUSIBLE_JALALI_MAX = 1600;
/** Rebuilds a Dayjs from Jalali fields, clamping the day into the target month. */
function build(w) {
    const jy = clampYear(w.jy);
    const jm = Math.min(12, Math.max(1, w.jm));
    const jd = Math.min(Math.max(1, w.jd), jalaliMonthLength(jy, jm));
    return dayjs(fromJalali(jy, jm, jd, w.hour, w.minute, w.second, w.millisecond, TZ));
}
function withDate(value, date) {
    const w = parts(value);
    return build({ ...w, jy: date.jy, jm: date.jm, jd: date.jd });
}
/* -------------------------------------------------------------------------- */
/* Formatting and parsing                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Longest-match-first so `MM` is never read as two `M`s. Mirrors the token set
 * `formatJalali` supports, plus the meridiem token rc-picker asks for when a
 * panel is in 12-hour mode.
 */
const TOKEN_PATTERN = /\[[^\]]*\]|MMMM|dddd|YYYY|YY|MM|DD|HH|mm|ss|dd|M|D|H|m|s|A|a/g;
/**
 * ── A PADDED TOKEN WANTS ALL ITS DIGITS ─────────────────────────────────────
 *
 * rc-picker parses the field on EVERY keystroke, and the moment a parse
 * succeeds it re-renders the input with the formatted value. With `DD` read as
 * one-or-two digits, «۱۳۷۰/۰۵/۲» — half-way through typing the form's own
 * example, ۱۳۷۰/۰۵/۲۰ — was already a valid date: the field repainted itself
 * as «۱۳۷۰/۰۵/۰۲», the next «۰» made «۱۳۷۰/۰۵/۰۲۰», which parses as nothing,
 * and on blur the picker kept the 2nd. A birth date silently off by eighteen
 * days, from a receptionist typing exactly what the helper told her to.
 *
 * So by default a padded token (`YYYY`, `MM`, `DD`, `HH`, `mm`, `ss`) matches
 * only its full width — the same reading dayjs gives `MM` — and a date is not
 * a date until it is complete. The unpadded tokens keep one-or-two digits.
 *
 * `lenient` relaxes the padded two-digit tokens to one-or-two, for the moment
 * typing is OVER: the single-date picker commits «۱۳۷۰/۵/۲۰» on blur or Enter
 * through it (see `jalali-picker.tsx`). The year stays four digits either way;
 * «۱۳۷» is an unfinished year, not the year 137.
 */
const NUMERIC_TOKENS = {
    YYYY: { key: 'jy', strict: '\\d{4}', lenient: '\\d{4}' },
    YY: { key: 'jy2', strict: '\\d{2}', lenient: '\\d{1,2}' },
    MM: { key: 'jm', strict: '\\d{2}', lenient: '\\d{1,2}' },
    M: { key: 'jm', strict: '\\d{1,2}', lenient: '\\d{1,2}' },
    DD: { key: 'jd', strict: '\\d{2}', lenient: '\\d{1,2}' },
    D: { key: 'jd', strict: '\\d{1,2}', lenient: '\\d{1,2}' },
    HH: { key: 'hour', strict: '\\d{2}', lenient: '\\d{1,2}' },
    H: { key: 'hour', strict: '\\d{1,2}', lenient: '\\d{1,2}' },
    mm: { key: 'minute', strict: '\\d{2}', lenient: '\\d{1,2}' },
    m: { key: 'minute', strict: '\\d{1,2}', lenient: '\\d{1,2}' },
    ss: { key: 'second', strict: '\\d{2}', lenient: '\\d{1,2}' },
    s: { key: 'second', strict: '\\d{1,2}', lenient: '\\d{1,2}' },
};
function escapeRegExp(input) {
    return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
/**
 * Turns a format string into a regex plus the ordered list of fields its groups
 * capture. Built per call rather than cached: formats are short, the picker
 * parses only on user input, and a cache keyed by format string is one more
 * thing to invalidate.
 */
function compileParser(format, lenient) {
    const fields = [];
    let source = '';
    let lastIndex = 0;
    for (const match of format.matchAll(TOKEN_PATTERN)) {
        const token = match[0];
        const index = match.index;
        source += escapeRegExp(format.slice(lastIndex, index));
        lastIndex = index + token.length;
        if (token.startsWith('[')) {
            source += escapeRegExp(token.slice(1, -1));
            continue;
        }
        if (token === 'MMMM') {
            source += '(\\S+)';
            fields.push('month-name');
            continue;
        }
        if (token === 'dddd' || token === 'dd') {
            source += '(\\S+)';
            fields.push('skip');
            continue;
        }
        if (token === 'A' || token === 'a') {
            source += '(\\S+)';
            fields.push('skip');
            continue;
        }
        const numeric = NUMERIC_TOKENS[token];
        if (numeric) {
            source += `(${lenient ? numeric.lenient : numeric.strict})`;
            fields.push(numeric.key);
            continue;
        }
        source += escapeRegExp(token);
    }
    source += escapeRegExp(format.slice(lastIndex));
    return { re: new RegExp(`^\\s*${source}\\s*$`), fields };
}
/** Persian text → an instant, or `null` when the text is not a valid Jalali date. */
export function parseJalaliInput(text, format, options = {}) {
    // Persian and Arabic-Indic digits are what a Persian keyboard actually
    // produces; refusing them would make the field unusable in production.
    const normalized = toEnglishDigits(text.trim());
    if (!normalized)
        return null;
    const { re, fields } = compileParser(format, options.lenient === true);
    const match = re.exec(normalized);
    if (!match)
        return null;
    const values = {};
    fields.forEach((field, i) => {
        const raw = match[i + 1];
        if (raw === undefined)
            return;
        if (field === 'skip')
            return;
        if (field === 'month-name') {
            const monthIndex = JALALI_MONTH_NAMES.findIndex((name) => name === raw);
            if (monthIndex >= 0)
                values.jm = monthIndex + 1;
            return;
        }
        values[field] = Number(raw);
    });
    const now = toJalali(new Date(), TZ);
    const jy = values.jy ?? (values.jy2 === undefined ? now.jy : 1400 + values.jy2);
    const jm = values.jm ?? 1;
    const jd = values.jd ?? 1;
    if (jy < MIN_JALALI_YEAR || jy > MAX_JALALI_YEAR)
        return null;
    if (jm < 1 || jm > 12)
        return null;
    if (jd < 1 || jd > jalaliMonthLength(jy, jm))
        return null;
    const hour = values.hour ?? 0;
    const minute = values.minute ?? 0;
    const second = values.second ?? 0;
    if (hour > 23 || minute > 59 || second > 59)
        return null;
    return dayjs(fromJalali(jy, jm, jd, hour, minute, second, 0, TZ));
}
/* -------------------------------------------------------------------------- */
/* The config                                                                 */
/* -------------------------------------------------------------------------- */
export const jalaliGenerateConfig = {
    getNow: () => dayjs(),
    getFixedDate: (fixed) => {
        // rc-picker hands this ISO-ish strings for internal anchors, and the
        // time-only panels use the Unix epoch (`1970-01-01`). The Jalali kernel's
        // valid range spans two millennia, so "is it in range" cannot disambiguate;
        // a *plausible* Jalali year can. Nothing this product shows is outside
        // 1200–1600 (1821–2221 CE), and no Gregorian anchor is inside it.
        const [y, m, d] = toEnglishDigits(fixed).split(/[-/]/).map(Number);
        if (y !== undefined && m !== undefined && d !== undefined) {
            if (y >= PLAUSIBLE_JALALI_MIN && y <= PLAUSIBLE_JALALI_MAX) {
                return build({ jy: y, jm: m, jd: d, hour: 0, minute: 0, second: 0, millisecond: 0 });
            }
            // A Gregorian anchor is a Tehran day too, not the browser's midnight.
            const g = gregorianToJalali(y, m || 1, d || 1);
            return build({ ...g, hour: 0, minute: 0, second: 0, millisecond: 0 });
        }
        return dayjs(fixed);
    },
    getEndDate: (value) => {
        const w = parts(value);
        return build({ ...w, jd: jalaliMonthLength(clampYear(w.jy), w.jm) });
    },
    // 0 = شنبه. Consistent with `getWeekFirstDay` and `getShortWeekDays` below,
    // which is what makes the header row and the grid agree.
    getWeekDay: (value) => toJalali(value.toDate(), TZ).weekday,
    getMillisecond: (value) => value.millisecond(),
    getSecond: (value) => parts(value).second,
    getMinute: (value) => parts(value).minute,
    getHour: (value) => parts(value).hour,
    getDate: (value) => parts(value).jd,
    getMonth: (value) => parts(value).jm - 1,
    getYear: (value) => parts(value).jy,
    addYear: (value, diff) => {
        const w = parts(value);
        const jy = clampYear(w.jy + diff);
        return build({ ...w, jy, jd: Math.min(w.jd, jalaliMonthLength(jy, w.jm)) });
    },
    addMonth: (value, diff) => withDate(value, addJalaliMonths(parts(value), diff)),
    addDate: (value, diff) => withDate(value, addJalaliDays(parts(value), diff)),
    setYear: (value, year) => {
        const w = parts(value);
        const jy = clampYear(year);
        return build({ ...w, jy, jd: Math.min(w.jd, jalaliMonthLength(jy, w.jm)) });
    },
    setMonth: (value, month) => {
        // `month` is 0-based and rc-picker may hand over -1 or 12 while stepping
        // across a year boundary, so normalise rather than clamp.
        const w = parts(value);
        const total = w.jy * 12 + month;
        const jy = clampYear(Math.floor(total / 12));
        const jm = (((total % 12) + 12) % 12) + 1;
        return build({ ...w, jy, jm, jd: Math.min(w.jd, jalaliMonthLength(jy, jm)) });
    },
    setDate: (value, date) => {
        const w = parts(value);
        // Same reason as `setMonth`: a day outside the month means "roll".
        return withDate(value, addJalaliDays({ jy: w.jy, jm: w.jm, jd: 1 }, date - 1));
    },
    setHour: (value, hour) => build({ ...parts(value), hour }),
    setMinute: (value, minute) => build({ ...parts(value), minute }),
    setSecond: (value, second) => build({ ...parts(value), second }),
    setMillisecond: (value, millisecond) => value.millisecond(millisecond),
    isAfter: (date1, date2) => date1.valueOf() > date2.valueOf(),
    isValidate: (date) => date.isValid(),
    locale: {
        getWeekFirstDay: () => 0,
        getWeekFirstDate: (_locale, value) => {
            const w = parts(value);
            const weekday = toJalali(value.toDate(), TZ).weekday;
            return withDate(value, addJalaliDays(w, -weekday));
        },
        getWeek: (_locale, value) => {
            const w = parts(value);
            const firstOfYear = { jy: w.jy, jm: 1, jd: 1 };
            const dayOfYear = jalaliDaysBetween(firstOfYear, w) + 1;
            const firstWeekday = toJalali(fromJalali(w.jy, 1, 1, 12, 0, 0, 0, TZ), TZ).weekday;
            return Math.floor((dayOfYear + firstWeekday - 1) / 7) + 1;
        },
        format: (_locale, date, format) => date && date.isValid() ? formatJalali(date.toDate(), format, { timeZone: TZ }) : '',
        parse: (_locale, text, formats) => {
            for (const format of formats) {
                const parsed = parseJalaliInput(text, format);
                if (parsed)
                    return parsed;
            }
            return null;
        },
        getShortWeekDays: () => [...PERSIAN_WEEKDAY_SHORT],
        getShortMonths: () => [...JALALI_MONTH_NAMES],
    },
};
/** The display formats the panels use. Jalali, Persian digits, zero-padded. */
export const JALALI_FORMATS = {
    date: 'YYYY/MM/DD',
    dateTime: 'YYYY/MM/DD HH:mm',
    month: 'MMMM YYYY',
    year: 'YYYY',
    time: 'HH:mm',
};
//# sourceMappingURL=jalali-generate-config.js.map